import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Alert, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Flight_FareRule } from "../services/flightService";
import "./Flights.css";

const getRouteLabel = (fareRuleItem) => {
  const rule = fareRuleItem?.rules?.[0];
  if (!rule?.Origin || !rule?.Destination) return "";
  return `${rule.Origin} → ${rule.Destination}`;
};

const FlightDetail = ({ flightContext, showModal, onHide }) => {
  const navigate = useNavigate();
  const [showFullRule, setShowFullRule] = useState(null);
  const {
    TraceId,
    tripType,
    isDomestic,
    resultIndexes,
    pricingBreakdown,
    routes = {},
  } = flightContext || {};

  const [fareRules, setFareRules] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedIndexes, setSelectedIndexes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ===============================
     FETCH FARE RULES
  =============================== */
  useEffect(() => {
    if (!showModal || !TraceId) return;

    const fetchFareRules = async () => {
      setLoading(true);
      setError(null);
      setFareRules([]);
      setSelectedIndexes({});
      setActiveTab(0);

      try {
        let indexesToCall = [];

        if (tripType === "round" && isDomestic) {
          indexesToCall = [resultIndexes?.outbound, resultIndexes?.inbound];
        } else {
          indexesToCall = Array.isArray(resultIndexes)
            ? resultIndexes
            : [resultIndexes];
        }

        const responses = [];

        for (const index of indexesToCall) {
          if (!index) continue;

          const res = await Flight_FareRule({
            TraceId,
            ResultIndex: index,
          });
          console.log("farerule reps", res);
          responses.push({
            ResultIndex: index,
            rules: res?.data?.Response?.FareRules || [],
          });
        }

        setFareRules(responses);
      } catch (err) {
        setError("Failed to fetch fare rules", err.response);
      } finally {
        setLoading(false);
      }
    };

    fetchFareRules();
  }, [showModal, TraceId, tripType, isDomestic, resultIndexes]);

  /* ===============================
     SELECTION
  =============================== */
  const handleSelect = (tabIndex, resultIndex) => {
    setSelectedIndexes((prev) => ({
      ...prev,
      [tabIndex]: resultIndex,
    }));
  };

  const canContinue =
    tripType === "round" && isDomestic
      ? selectedIndexes[0] && selectedIndexes[1]
      : selectedIndexes[0];

  /* ===============================
     CONTINUE
  =============================== */
  const handleContinue = () => {
    navigate("/flight-checkout", {
      state: {
        TraceId,
        tripType,
        isDomestic,
        pricingBreakdown,
        passengerCount: flightContext?.passengers,
        resultIndexes:
          tripType === "round" && isDomestic
            ? {
              outbound: selectedIndexes[0],
              inbound: selectedIndexes[1],
            }
            : selectedIndexes[0],
      },
    });

    onHide();
  };

  const extractFareSummary = (fareRule) => {
    const miniRules = fareRule?.MiniFarRules?.Rules || [];
    const currency = fareRule?.MiniFarRules?.Currency || "INR";

    const getFee = (type, departureType) => {
      const match = miniRules.find(
        (r) =>
          r.Type === type &&
          r.DepartureType.toLowerCase().includes(departureType.toLowerCase()),
      );
      return match?.PaxPenalties?.[0]?.AirlineFee ?? null;
    };

    const refundFee = getFee(0, "Before Departure");
    const changeFee = getFee(1, "Before Departure");
    const noShowFee = getFee(1, "No Show");

    return {
      airline: fareRule.Airline,
      departureTime: fareRule.DepartureTime,
      fareBasis: fareRule.FareBasisCode,
      origin: fareRule.Origin,
      destination: fareRule.Destination,
      route: `${fareRule.Origin} → ${fareRule.Destination}`,
      currency,
      refundFee,
      changeFee,
      noShowFee,
      isRefundable: refundFee !== null && refundFee !== 0,
      fullHTML: fareRule.FareRuleDetail,
    };
  };

  /* ===============================
     RENDER
  =============================== */
  return (
    <Modal
      show={showModal}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      dialogClassName="modern-flight-modal"
    >
      {/* ================= HEADER ================= */}
      <Modal.Header closeButton className="modern-modal-header">
        <div className="w-100">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="fw-bold mb-0 text-white">Flight Fare Options</h5>
            <Badge bg="primary">{tripType?.toUpperCase()}</Badge>
          </div>

          {isDomestic && tripType === "round" && (
            <Badge bg="secondary" className="mt-2">
              Domestic Return
            </Badge>
          )}

          <small className="plan-booking d-block mt-2">
            Please select plan to continue booking
          </small>
        </div>
      </Modal.Header>

      {/* ================= BODY ================= */}
      <Modal.Body className="modern-modal-body">
        {loading && (
          <div className="d-flex flex-column justify-content-center align-items-center py-5 w-100">
            <Spinner animation="border" />
            <p className="mt-2">Fetching fare rules…</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {/* ===== DEPART / RETURN TABS ===== */}
        {!loading && fareRules.length > 1 && (
          <div className="fare-tabs mb-4 d-flex gap-2 flex-wrap">
            {fareRules.map((item, idx) => {
              const routeLabel = getRouteLabel(item);

              return (
                <Button
                  key={idx}
                  className={`modern-tab-btn ${activeTab === idx ? "active" : ""
                    }`}
                  onClick={() => setActiveTab(idx)}
                >
                  {idx === 0
                    ? `DEPART : ${routeLabel}`
                    : `RETURN : ${routeLabel}`}
                </Button>
              );
            })}
          </div>
        )}

        {/* ===== FARE RULE CARDS ===== */}
        {!loading && fareRules.length > 0 && (
          <div className="fare-card-container">
            {fareRules.map((item, idx) => {
              const rule = item.rules?.[0];
              if (!rule) return null;

              const summary = extractFareSummary(rule);

              const isSelected = selectedIndexes[idx] === item.ResultIndex;

              return (
                <div
                  key={idx}
                  className={`fare-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(idx, item.ResultIndex)}
                >
                  {/* Label */}
                  <div className="fare-type-label">
                    {tripType === "round"
                      ? idx === 0
                        ? "DEPART FLIGHT"
                        : "RETURN FLIGHT"
                      : "FLIGHT"}
                  </div>

                  {/* Route */}
                  <div className="fare-route">
                    {summary.origin} → {summary.destination}
                  </div>

                  {/* Airline */}
                  <div className="fare-airline">Airline: {summary.airline}</div>

                  {/* Departure */}
                  <div className="fare-airline">
                    Departure:{" "}
                    {new Date(summary.departureTime).toLocaleString()}
                  </div>





                  {/* Read More */}
                  <Button
                    variant="link"
                    className="btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullRule((prev) => (prev === idx ? null : idx));
                    }}
                  >
                    {showFullRule === idx ? "Hide Details" : "Read More"}
                  </Button>

                  {showFullRule === idx && (
                    <div
                      className="fare-full-rule"
                      dangerouslySetInnerHTML={{
                        __html: summary.fullHTML,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* {!loading &&
          fareRules[activeTab] &&
          (fareRules[activeTab].rules.length > 0 ? (
            fareRules[activeTab].rules.map((rule, rIdx) => (
              <div
                key={rIdx}
                className={`modern-fare-card ${
                  selectedIndexes[activeTab] ===
                  fareRules[activeTab].ResultIndex
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  handleSelect(activeTab, fareRules[activeTab].ResultIndex)
                }
              >
                <div
                  className="fare-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      rule.FareRuleDetail ||
                      "<p>No detailed fare rule provided.</p>",
                  }}
                />
              </div>
            ))
          ) : (
            <div className="segment">
              No fare rules available for this segment.
            </div>
          ))} */}
      </Modal.Body>

      {/* ================= FOOTER ================= */}
      <Modal.Footer className="modern-modal-footer">
        <Button
          className="continue-btn-modern"
          onClick={handleContinue}
          disabled={!canContinue || loading}
        >
          Continue Booking →
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FlightDetail;
// new codse zero

// import React, { useEffect, useState } from "react";
// import { Modal, Button, Spinner, Alert } from "react-bootstrap";
// import { useNavigate } from "react-router-dom";
// import { Flight_FareRule } from "../services/flightService";
// import "./Flights.css";

// const FlightDetail = ({ flightContext, showModal, onHide }) => {
//   const navigate = useNavigate();

//   const {
//     TraceId,
//     tripType,
//     isDomestic,
//     resultIndexes,
//     pricingBreakdown,
//     fareOptionsMap = {},
//   } = flightContext || {};

//   const [fareRules, setFareRules] = useState([]);
//   const [activeSegmentTab, setActiveSegmentTab] = useState(0); // for round/multi segment tabs
//   const [activeCabinTab, setActiveCabinTab] = useState("Economy"); // Economy / Business
//   const [selectedFare, setSelectedFare] = useState(null); // { tabIndex, ResultIndex }
//   const [showFullRule, setShowFullRule] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // ─── Helpers ──────────────────────────────────────────────────────────────
//   const formatTime = (iso) => {
//     if (!iso) return "--:--";
//     try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }); }
//     catch { return "--:--"; }
//   };

//   const formatDate = (iso) => {
//     if (!iso) return "";
//     try { return new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "2-digit" }); }
//     catch { return ""; }
//   };

//   const formatDuration = (mins) => {
//     if (!mins && mins !== 0) return "--";
//     return `${Math.floor(mins / 60)}h ${mins % 60}m`;
//   };

//   const getDisplayPrice = (flight) => {
//     if (!flight) return 0;
//     if (flight.DisplayPrice != null) return flight.DisplayPrice;
//     if (flight.Fare?.OfferedFare != null) return flight.Fare.OfferedFare;
//     if (flight.Fare?.PublishedFare != null) return flight.Fare.PublishedFare;
//     return 0;
//   };

//   const fmt = (n) => n != null ? `₹ ${Math.ceil(n).toLocaleString("en-IN")}` : null;

//   // ─── Fetch Fare Rules ─────────────────────────────────────────────────────
//   useEffect(() => {
//     if (!showModal || !TraceId) return;

//     const fetchFareRules = async () => {
//       setLoading(true);
//       setError(null);
//       setFareRules([]);
//       setSelectedFare(null);
//       setActiveSegmentTab(0);

//       try {
//         let indexesToCall = [];
//         if (tripType === "round" && isDomestic) {
//           indexesToCall = [resultIndexes?.outbound, resultIndexes?.inbound];
//         } else {
//           indexesToCall = Array.isArray(resultIndexes) ? resultIndexes : [resultIndexes];
//         }

//         const responses = [];
//         for (const index of indexesToCall) {
//           if (!index) continue;
//           const res = await Flight_FareRule({ TraceId, ResultIndex: index });
//           responses.push({
//             ResultIndex: index,
//             rules: res?.data?.Response?.FareRules || [],
//           });
//         }
//         setFareRules(responses);
//       } catch (err) {
//         setError("Failed to fetch fare rules");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchFareRules();
//   }, [showModal, TraceId, tripType, isDomestic, resultIndexes]);

//   // ─── Current fare options for active segment tab ───────────────────────────
//   const allFareOptions = fareOptionsMap[activeSegmentTab] || [];

//   // ─── Group fare options by cabin class ────────────────────────────────────
//   const cabinGroups = allFareOptions.reduce((acc, fare) => {
//     const cabin = fare.Segments?.[0]?.[0]?.FlightCabinClass ||
//                   fare.Segments?.[0]?.FlightCabinClass || "Economy";
//     const cabinName = typeof cabin === "number"
//       ? (cabin === 1 ? "Economy" : cabin === 2 ? "Premium Economy" : cabin === 3 ? "Business" : "First Class")
//       : cabin;
//     if (!acc[cabinName]) acc[cabinName] = [];
//     acc[cabinName].push(fare);
//     return acc;
//   }, {});

//   // If grouping by cabin gives nothing, put everything in Economy
//   const cabinKeys = Object.keys(cabinGroups).length > 0 ? Object.keys(cabinGroups) : ["Economy"];
//   if (Object.keys(cabinGroups).length === 0) cabinGroups["Economy"] = allFareOptions;

//   const currentCabinFares = cabinGroups[activeCabinTab] || cabinGroups[cabinKeys[0]] || allFareOptions;

//   // ─── Flight header info ────────────────────────────────────────────────────
//   const representativeFlight = allFareOptions[0];
//   const seg = representativeFlight?.Segments?.[0];
//   const segData = (Array.isArray(seg) ? seg[0] : seg) || {};
//   const airline = segData.Airline || {};
//   const origin = segData.Origin || {};
//   const dest = segData.Destination || {};

//   // ─── Extract mini fare rules ───────────────────────────────────────────────
//   const extractMiniRules = (fare) => {
//     const miniRules = fare?.MiniFarRules?.Rules || [];
//     const getFee = (type, depType) => {
//       const match = miniRules.find(r =>
//         r.Type === type && r.DepartureType?.toLowerCase().includes(depType.toLowerCase())
//       );
//       return match?.PaxPenalties?.[0]?.AirlineFee ?? null;
//     };
//     return {
//       cancellation: getFee(0, "Before Departure"),
//       dateChange: getFee(1, "Before Departure"),
//       noShow: getFee(1, "No Show"),
//     };
//   };

//   // ─── Can continue ──────────────────────────────────────────────────────────
//   const canContinue = selectedFare !== null;

//   // ─── Continue ─────────────────────────────────────────────────────────────
//   const handleContinue = () => {
//     if (!selectedFare) return;
//     navigate("/flight-checkout", {
//       state: {
//         TraceId,
//         tripType,
//         isDomestic,
//         pricingBreakdown,
//         passengerCount: flightContext?.passengers,
//         resultIndexes:
//           tripType === "round" && isDomestic
//             ? { outbound: selectedFare, inbound: selectedFare }
//             : selectedFare,
//       },
//     });
//     onHide();
//   };

//   // ─── Segment tabs (round trip) ────────────────────────────────────────────
//   const segmentTabLabels = tripType === "round"
//     ? ["Departure", "Return"]
//     : fareRules.map((_, i) => `Segment ${i + 1}`);

//   // ─── Fare rule for current segment ────────────────────────────────────────
//   const currentFareRule = fareRules[activeSegmentTab]?.rules?.[0];

//   // ─── RENDER ───────────────────────────────────────────────────────────────
//   return (
//     <Modal
//       show={showModal}
//       onHide={onHide}
//       size="xl"
//       centered
//       scrollable
//       dialogClassName="mmt-flight-modal"
//     >
//       <Modal.Header closeButton style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px" }}>
//         <div style={{ width: "100%" }}>
//           {/* Flight header — MMT style */}
//           <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
//             <span style={{ fontSize: "16px", fontWeight: "700", color: "#1a202c" }}>
//               Flight Details and Fare Options available for you!
//             </span>
//           </div>

//           {representativeFlight && (
//             <div style={{
//               display: "flex", alignItems: "center", gap: "10px",
//               marginTop: "10px", flexWrap: "wrap",
//               fontSize: "13px", color: "#4a5568",
//             }}>
//               <span style={{ fontWeight: "600", color: "#1a202c" }}>
//                 {origin?.Airport?.CityName || origin?.Airport?.AirportCode || "--"}
//               </span>
//               <span style={{ color: "#718096" }}>→</span>
//               <span style={{ fontWeight: "600", color: "#1a202c" }}>
//                 {dest?.Airport?.CityName || dest?.Airport?.AirportCode || "--"}
//               </span>

//               {/* Airline logo placeholder */}
//               <div style={{
//                 background: "#fff3e0", color: "#e65100",
//                 padding: "2px 8px", borderRadius: "4px",
//                 fontWeight: "700", fontSize: "13px",
//               }}>
//                 {airline.AirlineCode || "--"}
//               </div>

//               <span style={{ color: "#718096" }}>{airline.AirlineName || ""}</span>
//               <span style={{ color: "#718096" }}>•</span>
//               <span>{formatDate(origin?.DepTime)}</span>
//               <span style={{ color: "#718096" }}>•</span>
//               <span>Departure at {formatTime(origin?.DepTime)} - Arrival at {formatTime(dest?.ArrTime)}</span>
//             </div>
//           )}

//           {/* Segment tabs for round/multi */}
//           {segmentTabLabels.length > 1 && (
//             <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
//               {segmentTabLabels.map((label, idx) => (
//                 <button key={idx} onClick={() => setActiveSegmentTab(idx)} style={{
//                   padding: "6px 16px", borderRadius: "20px",
//                   border: `2px solid ${activeSegmentTab === idx ? "#1a56db" : "#e2e8f0"}`,
//                   background: activeSegmentTab === idx ? "#eff6ff" : "#fff",
//                   color: activeSegmentTab === idx ? "#1a56db" : "#4a5568",
//                   fontWeight: "600", fontSize: "12px", cursor: "pointer",
//                 }}>
//                   {label}
//                 </button>
//               ))}
//             </div>
//           )}
//         </div>
//       </Modal.Header>

//       <Modal.Body style={{ padding: "0", background: "#f7f9fc", minHeight: "400px" }}>

//         {loading && (
//           <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 0" }}>
//             <Spinner animation="border" style={{ color: "#1a56db" }} />
//             <p style={{ marginTop: "12px", color: "#718096" }}>Fetching fare details…</p>
//           </div>
//         )}

//         {error && <Alert variant="danger" style={{ margin: "16px" }}>{error}</Alert>}

//         {!loading && (
//           <>
//             {/* ── Cabin Class Tabs ── */}
//             {cabinKeys.length > 0 && (
//               <div style={{
//                 background: "#fff",
//                 borderBottom: "1px solid #e2e8f0",
//                 padding: "0 24px",
//                 display: "flex", gap: "0",
//               }}>
//                 {cabinKeys.map((cabin) => {
//                   const cheapest = Math.min(...(cabinGroups[cabin] || []).map(getDisplayPrice).filter(p => p > 0));
//                   const isActive = activeCabinTab === cabin || (activeCabinTab === "Economy" && cabin === cabinKeys[0]);
//                   return (
//                     <button
//                       key={cabin}
//                       onClick={() => setActiveCabinTab(cabin)}
//                       style={{
//                         padding: "16px 24px", border: "none",
//                         background: "transparent", cursor: "pointer",
//                         borderBottom: isActive ? "3px solid #1a56db" : "3px solid transparent",
//                         transition: "all 0.15s",
//                       }}
//                     >
//                       <div style={{ fontWeight: "700", fontSize: "14px", color: isActive ? "#1a56db" : "#4a5568" }}>
//                         {cabin}
//                       </div>
//                       <div style={{ fontSize: "12px", color: "#718096", marginTop: "2px" }}>
//                         Starting at {cheapest > 0 ? `₹ ${Math.ceil(cheapest).toLocaleString("en-IN")}` : "--"}
//                       </div>
//                       {cabin === "Economy" && (
//                         <div style={{ fontSize: "11px", color: "#718096" }}>Affordable fares & value for money</div>
//                       )}
//                       {cabin === "Business" && (
//                         <div style={{ fontSize: "11px", color: "#718096" }}>Luxury experience with premium services</div>
//                       )}
//                     </button>
//                   );
//                 })}
//               </div>
//             )}

//             {/* ── Fare Cards — horizontal scroll ── */}
//             {allFareOptions.length > 0 ? (
//               <div style={{
//                 display: "flex", gap: "16px",
//                 overflowX: "auto", padding: "20px 24px",
//                 scrollbarWidth: "thin",
//               }}>
//                 {currentCabinFares.map((fare, idx) => {
//                   const price = getDisplayPrice(fare);
//                   const fareType = fare.FareClassification?.Type || fare.ResultFareType || "VALUE";
//                   const fareColor = fare.FareClassification?.Color || "#1a56db";
//                   const isRefundable = fare.IsRefundable;
//                   const hasMeal = fare.IsFreeMealAvailable;
//                   const hasFreeSeat = fare.IsSeatFree;
//                   const isExclusive = fare.IsExclusiveFare;
//                   const publishedPrice = fare.Fare?.PublishedFare || 0;
//                   const offeredPrice = fare.Fare?.OfferedFare || price;
//                   const savings = publishedPrice > offeredPrice ? publishedPrice - offeredPrice : 0;

//                   const fareSeg = fare?.Segments?.[0];
//                   const fareSegData = (Array.isArray(fareSeg) ? fareSeg[0] : fareSeg) || {};
//                   const baggage = fareSegData.Baggage || "15 Kg";
//                   const cabinBaggage = fareSegData.CabinBaggage || "7 Kg";

//                   const miniRules = extractMiniRules(fare);
//                   const isChosen = selectedFare === fare.ResultIndex;

//                   return (
//                     <div key={idx} style={{
//                       minWidth: "260px", maxWidth: "280px",
//                       background: "#fff",
//                       border: `2px solid ${isChosen ? "#1a56db" : "#e2e8f0"}`,
//                       borderRadius: "12px",
//                       boxShadow: isChosen ? "0 4px 20px rgba(26,86,219,0.18)" : "0 1px 4px rgba(0,0,0,0.06)",
//                       display: "flex", flexDirection: "column",
//                       overflow: "hidden", transition: "all 0.18s",
//                       cursor: "pointer",
//                       transform: isChosen ? "translateY(-2px)" : "none",
//                     }}
//                       onClick={() => setSelectedFare(isChosen ? null : fare.ResultIndex)}
//                     >
//                       {/* Card top — price + fare type */}
//                       <div style={{
//                         padding: "16px 16px 12px",
//                         borderBottom: "1px solid #f1f5f9",
//                         background: isChosen ? "#eff6ff" : "#fff",
//                       }}>
//                         {isExclusive && (
//                           <div style={{
//                             background: "#fef3c7", color: "#92400e",
//                             fontSize: "10px", fontWeight: "700",
//                             padding: "2px 8px", borderRadius: "4px",
//                             display: "inline-block", marginBottom: "6px",
//                           }}>
//                             ⭐ MMT SPECIAL
//                           </div>
//                         )}

//                         <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
//                           {savings > 0 && (
//                             <span style={{ fontSize: "12px", color: "#9ca3af", textDecoration: "line-through" }}>
//                               ₹ {Math.ceil(publishedPrice).toLocaleString("en-IN")}
//                             </span>
//                           )}
//                           <span style={{ fontSize: "22px", fontWeight: "800", color: "#1a202c" }}>
//                             ₹ {Math.ceil(offeredPrice).toLocaleString("en-IN")}
//                           </span>
//                         </div>
//                         <div style={{ fontSize: "12px", color: "#718096" }}>per adult</div>
//                         <div style={{
//                           display: "inline-block",
//                           marginTop: "6px",
//                           fontSize: "12px", fontWeight: "700",
//                           color: fareColor,
//                           letterSpacing: "0.5px",
//                         }}>
//                           {fareType.toUpperCase()}
//                         </div>
//                       </div>

//                       {/* Card body — details */}
//                       <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column", gap: "14px" }}>

//                         {/* Baggage */}
//                         <div>
//                           <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>
//                             Baggage
//                           </div>
//                           <div style={{ fontSize: "12px", color: "#4a5568", display: "flex", flexDirection: "column", gap: "3px" }}>
//                             <span>✅ {cabinBaggage} Cabin Baggage</span>
//                             <span>✅ {baggage} Check-in Baggage</span>
//                           </div>
//                         </div>

//                         {/* Flexibility */}
//                         <div>
//                           <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>
//                             Flexibility
//                           </div>
//                           <div style={{ fontSize: "12px", color: "#4a5568", display: "flex", flexDirection: "column", gap: "3px" }}>
//                             {miniRules.cancellation != null ? (
//                               <span style={{ color: miniRules.cancellation === 0 ? "#16a34a" : "#6b7280" }}>
//                                 {miniRules.cancellation === 0
//                                   ? "✅ Free Cancellation"
//                                   : `🔸 Cancellation fee starts at ${fmt(miniRules.cancellation)} (up to 2 hours before departure)`}
//                               </span>
//                             ) : (
//                               <span style={{ color: isRefundable ? "#16a34a" : "#dc2626" }}>
//                                 {isRefundable ? "✅ Refundable" : "🔴 Non-Refundable"}
//                               </span>
//                             )}
//                             {miniRules.dateChange != null && (
//                               <span style={{ color: miniRules.dateChange === 0 ? "#16a34a" : "#6b7280" }}>
//                                 {miniRules.dateChange === 0
//                                   ? "✅ Free Date Change"
//                                   : `🔸 Date Change fee starts at ${fmt(miniRules.dateChange)} up to 2 hrs before departure`}
//                               </span>
//                             )}
//                           </div>
//                         </div>

//                         {/* Seats, Meals */}
//                         <div>
//                           <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>
//                             Seats, Meals & More
//                           </div>
//                           <div style={{ fontSize: "12px", color: "#4a5568", display: "flex", flexDirection: "column", gap: "3px" }}>
//                             <span style={{ color: hasFreeSeat ? "#16a34a" : "#6b7280" }}>
//                               {hasFreeSeat ? "✅ Free Seats" : "🔸 Chargeable Seats"}
//                             </span>
//                             <span style={{ color: hasMeal ? "#16a34a" : "#6b7280" }}>
//                               {hasMeal ? "✅ Complimentary Meals" : "🔸 Chargeable Meals"}
//                             </span>
//                           </div>
//                         </div>

//                         {/* Savings badge */}
//                         {savings > 0 && (
//                           <div style={{
//                             background: "#fef9ec", border: "1px solid #fbbf24",
//                             borderRadius: "6px", padding: "6px 10px",
//                             fontSize: "11px", color: "#92400e", fontWeight: "600",
//                           }}>
//                             💰 Save ₹{Math.ceil(savings).toLocaleString("en-IN")} on this fare
//                           </div>
//                         )}
//                       </div>

//                       {/* Card footer — buttons */}
//                       <div style={{
//                         padding: "12px 16px",
//                         borderTop: "1px solid #f1f5f9",
//                         display: "flex", gap: "8px",
//                       }}>
//                         <button
//                           onClick={(e) => { e.stopPropagation(); setSelectedFare(isChosen ? null : fare.ResultIndex); }}
//                           style={{
//                             flex: 1, padding: "9px 0",
//                             border: `2px solid ${isChosen ? "#16a34a" : "#1a56db"}`,
//                             borderRadius: "8px",
//                             background: isChosen ? "#f0fdf4" : "#fff",
//                             color: isChosen ? "#16a34a" : "#1a56db",
//                             fontWeight: "700", fontSize: "12px",
//                             cursor: "pointer", transition: "all 0.15s",
//                           }}
//                         >
//                           {isChosen ? "✓ SELECTED" : "LOCK PRICE"}
//                         </button>
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             setSelectedFare(fare.ResultIndex);
//                             // Navigate immediately on BOOK NOW
//                             setTimeout(() => {
//                               navigate("/flight-checkout", {
//                                 state: {
//                                   TraceId, tripType, isDomestic,
//                                   pricingBreakdown,
//                                   passengerCount: flightContext?.passengers,
//                                   resultIndexes:
//                                     tripType === "round" && isDomestic
//                                       ? { outbound: fare.ResultIndex, inbound: fare.ResultIndex }
//                                       : fare.ResultIndex,
//                                 },
//                               });
//                               onHide();
//                             }, 50);
//                           }}
//                           style={{
//                             flex: 1, padding: "9px 0",
//                             border: "none",
//                             borderRadius: "8px",
//                             background: "linear-gradient(135deg, #1a56db, #0e3fa8)",
//                             color: "#fff",
//                             fontWeight: "700", fontSize: "12px",
//                             cursor: "pointer", transition: "all 0.15s",
//                           }}
//                         >
//                           BOOK NOW
//                         </button>
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>
//             ) : (
//               <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
//                 <p>No fare options available.</p>
//               </div>
//             )}

//             {/* ── Fare Rules section ── */}
//             {currentFareRule && (
//               <div style={{ margin: "0 24px 24px", background: "#fff", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
//                 <div
//                   style={{
//                     padding: "14px 18px", cursor: "pointer",
//                     display: "flex", justifyContent: "space-between", alignItems: "center",
//                     borderBottom: showFullRule ? "1px solid #e2e8f0" : "none",
//                   }}
//                   onClick={() => setShowFullRule(!showFullRule)}
//                 >
//                   <div>
//                     <span style={{ fontWeight: "700", fontSize: "13px", color: "#374151" }}>
//                       Fare Rules & Policies
//                     </span>
//                     <span style={{ marginLeft: "8px", fontSize: "12px", color: "#718096" }}>
//                       {currentFareRule.Origin} → {currentFareRule.Destination} • {currentFareRule.Airline}
//                     </span>
//                   </div>
//                   <span style={{ fontSize: "12px", color: "#1a56db", fontWeight: "600" }}>
//                     {showFullRule ? "▲ Hide" : "▼ View Rules"}
//                   </span>
//                 </div>
//                 {showFullRule && currentFareRule.FareRuleDetail && (
//                   <div
//                     style={{
//                       padding: "16px 18px", fontSize: "12px", color: "#374151",
//                       maxHeight: "300px", overflowY: "auto",
//                     }}
//                     dangerouslySetInnerHTML={{ __html: currentFareRule.FareRuleDetail }}
//                   />
//                 )}
//               </div>
//             )}
//           </>
//         )}
//       </Modal.Body>

//       <Modal.Footer style={{ background: "#fff", borderTop: "1px solid #e2e8f0", padding: "14px 24px" }}>
//         <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
//           <div style={{ fontSize: "13px", color: "#718096" }}>
//             {selectedFare
//               ? <span style={{ color: "#16a34a", fontWeight: "600" }}>✓ Fare selected — ready to book</span>
//               : "Select a fare option above to continue"}
//           </div>
//           <Button
//             onClick={handleContinue}
//             disabled={!canContinue || loading}
//             style={{
//               background: canContinue ? "linear-gradient(135deg, #1a56db, #0e3fa8)" : "#e2e8f0",
//               border: "none", borderRadius: "8px",
//               padding: "10px 28px", fontWeight: "700", fontSize: "14px",
//               color: canContinue ? "#fff" : "#9ca3af",
//               cursor: canContinue ? "pointer" : "not-allowed",
//             }}
//           >
//             Continue Booking →
//           </Button>
//         </div>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default FlightDetail;



// new code 


// import React, { useEffect, useState, useRef } from "react";
// import { Modal, Button, Spinner, Alert } from "react-bootstrap";
// import { useNavigate } from "react-router-dom";
// import { Flight_FareRule } from "../services/flightService";
// import "./Flights.css";

// const FlightDetail = ({ flightContext, showModal, onHide }) => {
//   const navigate = useNavigate();
//   const scrollContainerRef = useRef(null);

//   const {
//     TraceId,
//     tripType,
//     isDomestic,
//     resultIndexes,
//     pricingBreakdown,
//     fareOptionsMap = {},
//     passengers,
//     isFareSelection = false,
//     groupedFlight = null,
//   } = flightContext || {};

//   const [fareRules, setFareRules] = useState([]);
//   const [activeSegmentTab, setActiveSegmentTab] = useState(0);
//   const [selectedFare, setSelectedFare] = useState(null);
//   const [expandedFareRule, setExpandedFareRule] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [allFareOptions, setAllFareOptions] = useState([]);
//   const [fareRulesMap, setFareRulesMap] = useState({});
//   const [activeCabinTab, setActiveCabinTab] = useState("all");

//   // ─── Initialize with all fare options from grouped flight ─────────────────
//   useEffect(() => {
//     if (showModal && isFareSelection && groupedFlight) {
//       setAllFareOptions(groupedFlight.fareOptions || []);
//       setLoading(false);
//     } else if (showModal && !isFareSelection && TraceId) {
//       fetchFareRules();
//     }
//   }, [showModal, isFareSelection, groupedFlight, TraceId]);

//   // ─── Fetch Fare Rules for a specific option ───────────────────────────────
//   const fetchFareRulesForOption = async (resultIndex) => {
//     if (!TraceId && !groupedFlight?.TraceId) return;
    
//     try {
//       const res = await Flight_FareRule({ 
//         TraceId: TraceId || groupedFlight?.TraceId, 
//         ResultIndex: resultIndex 
//       });
      
//       setFareRulesMap(prev => ({
//         ...prev,
//         [resultIndex]: res?.data?.Response?.FareRules || []
//       }));
//     } catch (err) {
//       console.error("Failed to fetch fare rules for option:", err);
//     }
//   };

//   // ─── Fetch Fare Rules (for existing flow) ────────────────────────────────
//   const fetchFareRules = async () => {
//     setLoading(true);
//     setError(null);
//     setFareRules([]);
//     setSelectedFare(null);
//     setActiveSegmentTab(0);

//     try {
//       let indexesToCall = [];
//       if (tripType === "round" && isDomestic) {
//         indexesToCall = [resultIndexes?.outbound, resultIndexes?.inbound];
//       } else {
//         indexesToCall = Array.isArray(resultIndexes) ? resultIndexes : [resultIndexes];
//       }

//       const responses = [];
//       for (const index of indexesToCall) {
//         if (!index) continue;
//         const res = await Flight_FareRule({ TraceId, ResultIndex: index });
//         responses.push({
//           ResultIndex: index,
//           rules: res?.data?.Response?.FareRules || [],
//         });
//       }
//       setFareRules(responses);
//     } catch (err) {
//       setError("Failed to fetch fare rules");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ─── Helpers ──────────────────────────────────────────────────────────────
//   const formatTime = (iso) => {
//     if (!iso) return "--:--";
//     try { 
//       const date = new Date(iso);
//       return date.toLocaleTimeString("en-IN", { 
//         hour: "2-digit", 
//         minute: "2-digit", 
//         hour12: false 
//       }); 
//     } catch { 
//       return "--:--"; 
//     }
//   };

//   const formatDate = (iso) => {
//     if (!iso) return "";
//     try { 
//       const date = new Date(iso);
//       return date.toLocaleDateString("en-IN", { 
//         weekday: "short", 
//         day: "numeric", 
//         month: "short", 
//         year: "2-digit" 
//       }); 
//     } catch { 
//       return ""; 
//     }
//   };

//   const getDisplayPrice = (flight) => {
//     if (!flight) return 0;
//     if (flight.DisplayPrice != null) return flight.DisplayPrice;
//     if (flight.Pricing?.finalAmount != null) return flight.Pricing.finalAmount;
//     if (flight.Fare?.OfferedFare != null) return flight.Fare.OfferedFare;
//     if (flight.Fare?.PublishedFare != null) return flight.Fare.PublishedFare;
//     return 0;
//   };

//   const getOriginalPrice = (flight) => {
//     if (!flight) return 0;
//     if (flight.Fare?.PublishedFare != null) return flight.Fare.PublishedFare;
//     return getDisplayPrice(flight);
//   };

//   const fmt = (n) => n != null ? `₹ ${Math.ceil(n).toLocaleString("en-IN")}` : null;

//   // ─── Get flight info from first fare option ──────────────────────────────
//   const representativeFlight = isFareSelection ? allFareOptions[0] : null;
//   const seg = representativeFlight?.Segments?.[0];
//   const segData = (Array.isArray(seg) ? seg[0] : seg) || {};
//   const airline = segData.Airline || {};
//   const origin = segData.Origin || {};
//   const dest = segData.Destination || {};

//   // Format route
//   const originCity = origin?.Airport?.CityName || origin?.city || "Delhi";
//   const destCity = dest?.Airport?.CityName || dest?.city || "Navi Mumbai";
//   const originCode = origin?.Airport?.AirportCode || origin?.code || "DEL";
//   const destCode = dest?.Airport?.AirportCode || dest?.code || "BOM";

//   // ─── Group fare options by cabin class ────────────────────────────────────
//   const cabinGroups = allFareOptions.reduce((acc, fare) => {
//     const cabin = fare.FareClassification?.CabinClass || 
//                   fare.CabinClass || 
//                   "Economy";
//     if (!acc[cabin]) acc[cabin] = [];
//     acc[cabin].push(fare);
//     return acc;
//   }, {});

//   const cabinKeys = ["all", ...Object.keys(cabinGroups)];
  
//   const getFilteredFares = () => {
//     if (activeCabinTab === "all") return allFareOptions;
//     return cabinGroups[activeCabinTab] || [];
//   };

//   // ─── Extract fare rules details ─────────────────────────────────────────
//   const extractFareDetails = (fare) => {
//     const miniRules = fare?.MiniFareRules?.[0] || [];
    
//     // Find cancellation rules
//     const cancellationRules = miniRules.filter(r => 
//       r.Type === "Cancellation" || r.Type?.toLowerCase().includes("cancel")
//     );
    
//     // Find reissue/change rules
//     const changeRules = miniRules.filter(r => 
//       r.Type === "Reissue" || r.Type?.toLowerCase().includes("reissue") || r.Type?.toLowerCase().includes("change")
//     );

//     // Get baggage info
//     const fareBreakdown = fare.FareBreakdown?.[0];
//     const segmentDetails = fareBreakdown?.SegmentDetails?.[0] || {};
//     const cabinBaggage = segmentDetails.CabinBaggage?.Value || 
//                         segmentDetails.CabinBaggage?.FreeText || 
//                         fare.CabinBaggage || "7 Kg";
//     const checkinBaggage = segmentDetails.CheckedInBaggage?.Value || 
//                           segmentDetails.CheckedInBaggage?.FreeText || 
//                           fare.Baggage || "15 Kg";

//     // Get fare type
//     const fareType = fare.FareClassification?.Type || 
//                     fare.ResultFareType || 
//                     fare.SupplierFareClass || 
//                     "REGULAR";

//     // Check if seats are free
//     const isSeatFree = fare.IsSeatFree || fare.IsFreeSeatAvailable || false;
    
//     // Check if meals are free
//     const isMealFree = fare.IsFreeMealAvailable || false;

//     // Check if fare is by MMT
//     const isMMTFare = fare.Source === 4 || fare.SourceName === "MMT" || fare.IsExclusiveFare;

//     // Get cancellation text
//     let cancellationText = "❌ No refund on Cancellation";
//     if (fare.IsRefundable) {
//       cancellationText = "✅ Free Cancellation";
//     } else if (cancellationRules.length > 0) {
//       const rule = cancellationRules[0];
//       const fee = rule.Details.match(/INR \d+/)?.[0] || rule.Details;
//       cancellationText = `❌ Cancellation fee starts at ${fee}`;
//     }

//     // Get date change text
//     let dateChangeText = null;
//     if (changeRules.length > 0) {
//       const rule = changeRules[0];
//       const fee = rule.Details.match(/INR \d+/)?.[0] || rule.Details;
//       dateChangeText = `❌ Date Change fee starts at ${fee}`;
//     }

//     return {
//       fareType,
//       isMMTFare,
//       cabinBaggage: String(cabinBaggage).replace(/[^0-9]/g, '') + " Kg",
//       checkinBaggage: String(checkinBaggage).replace(/[^0-9]/g, '') + " Kg",
//       cancellationText,
//       dateChangeText,
//       isSeatFree,
//       isMealFree,
//       isRefundable: fare.IsRefundable,
//       hasSavings: fare.Fare?.PublishedFare > fare.Fare?.OfferedFare
//     };
//   };

//   // ─── Get savings amount ──────────────────────────────────────────────────
//   const getSavings = (fare) => {
//     if (!fare.Fare) return 0;
//     const published = fare.Fare.PublishedFare || 0;
//     const offered = fare.Fare.OfferedFare || fare.DisplayPrice || 0;
//     return published > offered ? published - offered : 0;
//   };

//   // ─── Can continue ────────────────────────────────────────────────────────
//   const canContinue = selectedFare !== null;

//   // ─── Continue ────────────────────────────────────────────────────────────
//   const handleContinue = () => {
//     if (!selectedFare) return;
    
//     const selectedFareData = allFareOptions.find(f => f.ResultIndex === selectedFare);
    
//     navigate("/flight-checkout", {
//       state: {
//         TraceId: selectedFareData?.TraceId || TraceId,
//         tripType,
//         isDomestic,
//         pricingBreakdown: selectedFareData?.Pricing || pricingBreakdown,
//         passengers,
//         resultIndexes: selectedFare,
//       },
//     });
//     onHide();
//   };

//   // ─── Toggle fare rules expansion ────────────────────────────────────────
//   const toggleFareRules = (resultIndex, e) => {
//     e.stopPropagation();
//     setExpandedFareRule(expandedFareRule === resultIndex ? null : resultIndex);
    
//     if (!fareRulesMap[resultIndex] && (TraceId || groupedFlight?.TraceId)) {
//       fetchFareRulesForOption(resultIndex);
//     }
//   };

//   // ─── Scroll functions ────────────────────────────────────────────────────
//   const scrollLeft = () => {
//     if (scrollContainerRef.current) {
//       scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
//     }
//   };

//   const scrollRight = () => {
//     if (scrollContainerRef.current) {
//       scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
//     }
//   };

//   // ─── Render fare option card (HORIZONTAL CARD) ───────────────────────────
//   const renderFareCard = (fare, index) => {
//     const details = extractFareDetails(fare);
//     const displayPrice = getDisplayPrice(fare);
//     const originalPrice = getOriginalPrice(fare);
//     const savings = getSavings(fare);
//     const isSelected = selectedFare === fare.ResultIndex;
//     const isExpanded = expandedFareRule === fare.ResultIndex;

//     return (
//       <div 
//         key={index}
//         className="fare-card-horizontal"
//         style={{
//           minWidth: "320px",
//           maxWidth: "320px",
//           height: "fit-content",
//           border: `2px solid ${isSelected ? "#1a56db" : "#e9ecef"}`,
//           borderRadius: "12px",
//           background: "#fff",
//           boxShadow: isSelected ? "0 4px 12px rgba(26,86,219,0.15)" : "0 2px 8px rgba(0,0,0,0.05)",
//           cursor: "pointer",
//           transition: "all 0.2s",
//           display: "flex",
//           flexDirection: "column",
//           marginRight: "16px",
//           flexShrink: 0,
//         }}
//         onClick={() => setSelectedFare(isSelected ? null : fare.ResultIndex)}
//       >
//         {/* Card Content */}
//         <div style={{ padding: "16px" }}>
//           {/* Price Row */}
//           <div style={{ marginBottom: "12px" }}>
//             <div style={{ display: "flex", alignItems: "baseline", gap: "6px", flexWrap: "wrap" }}>
//               {savings > 0 && (
//                 <span style={{ 
//                   fontSize: "12px", 
//                   color: "#94a3b8",
//                   textDecoration: "line-through"
//                 }}>
//                   {fmt(originalPrice)}
//                 </span>
//               )}
//               <span style={{ 
//                 fontSize: "20px", 
//                 fontWeight: "700", 
//                 color: "#1a202c"
//               }}>
//                 {fmt(displayPrice)}
//               </span>
//             </div>
//             <span style={{ fontSize: "12px", color: "#64748b" }}>
//               per adult
//             </span>
//           </div>

//           {/* Fare Type */}
//           <div style={{ 
//             fontSize: "16px", 
//             fontWeight: "600", 
//             color: details.isMMTFare ? "#ff6b00" : "#0066cc",
//             marginBottom: "12px",
//             display: "flex",
//             alignItems: "center",
//             gap: "6px",
//             flexWrap: "wrap"
//           }}>
//             {details.fareType}
//             {details.isMMTFare && (
//               <span style={{ 
//                 fontSize: "10px",
//                 background: "#fff3e0",
//                 color: "#ff6b00",
//                 padding: "2px 6px",
//                 borderRadius: "4px",
//                 fontWeight: "500",
//                 whiteSpace: "nowrap"
//               }}>
//                 MMT SPECIAL
//               </span>
//             )}
//           </div>

//           {/* Baggage Section */}
//           <div style={{ marginBottom: "12px" }}>
//             <div style={{ 
//               fontSize: "12px", 
//               fontWeight: "600", 
//               color: "#334155",
//               marginBottom: "6px"
//             }}>
//               Baggage
//             </div>
//             <div style={{ 
//               fontSize: "12px",
//               color: "#475569",
//               lineHeight: "1.6"
//             }}>
//               <div>✅ {details.cabinBaggage} Cabin</div>
//               <div>✅ {details.checkinBaggage} Check-in</div>
//             </div>
//           </div>

//           {/* Flexibility Section */}
//           <div style={{ marginBottom: "12px" }}>
//             <div style={{ 
//               fontSize: "12px", 
//               fontWeight: "600", 
//               color: "#334155",
//               marginBottom: "6px"
//             }}>
//               Flexibility
//             </div>
//             <div style={{ 
//               fontSize: "12px",
//               color: details.isRefundable ? "#16a34a" : "#dc2626",
//               lineHeight: "1.6"
//             }}>
//               <div>{details.cancellationText}</div>
//               {details.dateChangeText && (
//                 <div style={{ color: "#dc2626", marginTop: "4px" }}>
//                   {details.dateChangeText}
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Seats, Meals & More Section */}
//           <div style={{ marginBottom: "12px" }}>
//             <div style={{ 
//               fontSize: "12px", 
//               fontWeight: "600", 
//               color: "#334155",
//               marginBottom: "6px"
//             }}>
//               Seats, Meals & More
//             </div>
//             <div style={{ 
//               fontSize: "12px",
//               color: "#475569",
//               lineHeight: "1.6"
//             }}>
//               <div style={{ color: details.isSeatFree ? "#16a34a" : "#64748b" }}>
//                 {details.isSeatFree ? "✅ Free Seats" : "✅ Chargeable Seats"}
//               </div>
//               <div style={{ color: details.isMealFree ? "#16a34a" : "#64748b" }}>
//                 {details.isMealFree ? "✅ Complimentary Meals" : "✅ Chargeable Meals"}
//               </div>
//             </div>
//           </div>

//           {/* View Details Button */}
//           <button
//             onClick={(e) => toggleFareRules(fare.ResultIndex, e)}
//             style={{
//               background: "none",
//               border: "none",
//               color: "#0066cc",
//               fontSize: "12px",
//               fontWeight: "500",
//               cursor: "pointer",
//               padding: "4px 0",
//               textDecoration: "underline",
//               display: "flex",
//               alignItems: "center",
//               gap: "4px",
//               marginBottom: "8px"
//             }}
//           >
//             {isExpanded ? "▲ Hide Details" : "▼ View Details"}
//           </button>

//           {/* Expanded Fare Rules */}
//           {isExpanded && fareRulesMap[fare.ResultIndex]?.length > 0 && (
//             <div style={{
//               marginTop: "8px",
//               padding: "12px",
//               background: "#f8fafc",
//               borderRadius: "8px",
//               fontSize: "11px",
//               border: "1px solid #e2e8f0",
//               maxHeight: "150px",
//               overflowY: "auto"
//             }}>
//               {fareRulesMap[fare.ResultIndex].map((rule, idx) => (
//                 <div key={idx}>
//                   {rule.FareRuleDetail ? (
//                     <div 
//                       dangerouslySetInnerHTML={{ __html: rule.FareRuleDetail }}
//                       style={{ color: "#475569" }}
//                     />
//                   ) : (
//                     <div style={{ color: "#64748b" }}>
//                       <div><strong>Route:</strong> {rule.Origin} → {rule.Destination}</div>
//                       <div><strong>Fare Basis:</strong> {rule.FareBasisCode}</div>
//                     </div>
//                   )}
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Card Footer - Actions */}
//         <div style={{
//           padding: "12px 16px",
//           borderTop: "1px solid #e9ecef",
//           display: "flex",
//           gap: "8px",
//           background: "#f8fafc",
//           borderBottomLeftRadius: "12px",
//           borderBottomRightRadius: "12px"
//         }}>
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               setSelectedFare(isSelected ? null : fare.ResultIndex);
//             }}
//             style={{
//               flex: 1,
//               padding: "8px 0",
//               border: `2px solid ${isSelected ? "#16a34a" : "#0066cc"}`,
//               borderRadius: "6px",
//               background: isSelected ? "#f0fdf4" : "#fff",
//               color: isSelected ? "#16a34a" : "#0066cc",
//               fontWeight: "600",
//               fontSize: "12px",
//               cursor: "pointer"
//             }}
//           >
//             {isSelected ? "✓ SELECTED" : "SELECT"}
//           </button>
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               handleContinue();
//             }}
//             style={{
//               flex: 1,
//               padding: "8px 0",
//               border: "none",
//               borderRadius: "6px",
//               background: "linear-gradient(135deg, #0066cc, #004999)",
//               color: "#fff",
//               fontWeight: "600",
//               fontSize: "12px",
//               cursor: "pointer"
//             }}
//           >
//             BOOK
//           </button>
//         </div>
//       </div>
//     );
//   };

//   // ─── Render flight header ────────────────────────────────────────────────
//   const renderHeader = () => {
//     if (!isFareSelection || !representativeFlight) return null;

//     return (
//       <div style={{
//         padding: "20px 24px 16px",
//         borderBottom: "1px solid #e9ecef",
//         background: "#fff"
//       }}>
//         <div style={{ 
//           fontSize: "18px", 
//           fontWeight: "600", 
//           color: "#1a202c",
//           marginBottom: "8px"
//         }}>
//           Flight Details and Fare Options available for you!
//         </div>

//         <div style={{
//           display: "flex",
//           alignItems: "center",
//           gap: "12px",
//           flexWrap: "wrap",
//           fontSize: "14px",
//           color: "#4a5568"
//         }}>
//           <span style={{ fontWeight: "600", color: "#1a202c" }}>
//             {originCity} → {destCity}
//           </span>
          
//           <span>•</span>
          
//           <span>
//             {airline.AirlineName || "Air India Express"}
//           </span>
          
//           <span>•</span>
          
//           <span>
//             {formatDate(origin?.DepTime)} • {formatTime(origin?.DepTime)} - {formatTime(dest?.ArrTime)}
//           </span>
//         </div>
//       </div>
//     );
//   };

//   // ─── Render cabin tabs ───────────────────────────────────────────────────
//   const renderCabinTabs = () => {
//     const filteredFares = getFilteredFares();
    
//     return (
//       <div style={{
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "space-between",
//         padding: "16px 24px",
//         borderBottom: "1px solid #e9ecef",
//         background: "#fff"
//       }}>
//         <div style={{ display: "flex", gap: "8px" }}>
//           {cabinKeys.map((cabin) => {
//             const count = cabin === "all" 
//               ? allFareOptions.length 
//               : cabinGroups[cabin]?.length || 0;
            
//             return (
//               <button
//                 key={cabin}
//                 onClick={() => setActiveCabinTab(cabin)}
//                 style={{
//                   padding: "8px 16px",
//                   border: "none",
//                   borderRadius: "20px",
//                   background: activeCabinTab === cabin ? "#0066cc" : "#f1f5f9",
//                   color: activeCabinTab === cabin ? "#fff" : "#334155",
//                   fontWeight: "500",
//                   fontSize: "13px",
//                   cursor: "pointer",
//                   transition: "all 0.2s"
//                 }}
//               >
//                 {cabin === "all" ? "All Fares" : cabin} ({count})
//               </button>
//             );
//           })}
//         </div>

//         {/* Scroll Buttons */}
//         <div style={{ display: "flex", gap: "8px" }}>
//           <button
//             onClick={scrollLeft}
//             style={{
//               width: "32px",
//               height: "32px",
//               borderRadius: "50%",
//               border: "1px solid #e2e8f0",
//               background: "#fff",
//               cursor: "pointer",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               fontSize: "18px"
//             }}
//           >
//             ←
//           </button>
//           <button
//             onClick={scrollRight}
//             style={{
//               width: "32px",
//               height: "32px",
//               borderRadius: "50%",
//               border: "1px solid #e2e8f0",
//               background: "#fff",
//               cursor: "pointer",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               fontSize: "18px"
//             }}
//           >
//             →
//           </button>
//         </div>
//       </div>
//     );
//   };

//   // ─── Render fare options in horizontal scroll ────────────────────────────
//   const renderFareOptions = () => {
//     if (allFareOptions.length === 0) {
//       return (
//         <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
//           <p>No fare options available.</p>
//         </div>
//       );
//     }

//     const filteredFares = getFilteredFares();
    
//     // Sort fares by price (lowest first)
//     const sortedFares = [...filteredFares].sort((a, b) => 
//       getDisplayPrice(a) - getDisplayPrice(b)
//     );

//     return (
//       <>
//         {renderCabinTabs()}
        
//         <div style={{ position: "relative", padding: "20px 24px" }}>
//           {/* Horizontal Scroll Container */}
//           <div
//             ref={scrollContainerRef}
//             style={{
//               display: "flex",
//               overflowX: "auto",
//               scrollBehavior: "smooth",
//               padding: "4px 4px 12px 4px",
//               gap: "4px",
//               scrollbarWidth: "thin",
//               msOverflowStyle: "auto",
//               WebkitOverflowScrolling: "touch"
//             }}
//             className="fare-scroll-container"
//           >
//             {sortedFares.map((fare, index) => renderFareCard(fare, index))}
//           </div>

//           {/* Gradient Fade Indicators */}
//           <div style={{
//             position: "absolute",
//             left: 0,
//             top: 20,
//             bottom: 20,
//             width: "40px",
//             background: "linear-gradient(to right, #f8f9fa, transparent)",
//             pointerEvents: "none"
//           }} />
//           <div style={{
//             position: "absolute",
//             right: 0,
//             top: 20,
//             bottom: 20,
//             width: "40px",
//             background: "linear-gradient(to left, #f8f9fa, transparent)",
//             pointerEvents: "none"
//           }} />
//         </div>

//         {/* Fare count indicator */}
//         <div style={{
//           padding: "0 24px 16px",
//           fontSize: "13px",
//           color: "#64748b",
//           borderBottom: "1px solid #e9ecef"
//         }}>
//           Showing {sortedFares.length} of {allFareOptions.length} fare options
//         </div>
//       </>
//     );
//   };

//   // ─── Render existing fare rules mode ────────────────────────────────────
//   const renderFareRulesMode = () => {
//     if (loading) {
//       return (
//         <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 0" }}>
//           <Spinner animation="border" style={{ color: "#0066cc" }} />
//           <p style={{ marginTop: "12px", color: "#718096" }}>Fetching fare details…</p>
//         </div>
//       );
//     }

//     if (error) {
//       return <Alert variant="danger" style={{ margin: "16px" }}>{error}</Alert>;
//     }

//     return (
//       <div style={{ padding: "24px" }}>
//         <h5>Fare Rules</h5>
//         {/* Add your existing fare rules display here */}
//       </div>
//     );
//   };

//   // ─── MAIN RENDER ────────────────────────────────────────────────────────
//   return (
//     <Modal
//       show={showModal}
//       onHide={onHide}
//       size="xl"
//       centered
//       scrollable
//       dialogClassName="modern-flight-modal"
//       style={{ 
//         fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
//       }}
//     >
//       {/* Header */}
//       {renderHeader()}

//       <Modal.Body style={{ padding: "0", background: "#f8f9fa" }}>
//         {isFareSelection ? renderFareOptions() : renderFareRulesMode()}
//       </Modal.Body>

//       <Modal.Footer style={{ 
//         background: "#fff", 
//         borderTop: "1px solid #e9ecef", 
//         padding: "16px 24px",
//         justifyContent: "space-between"
//       }}>
//         <div style={{ fontSize: "14px", color: "#4a5568" }}>
//           {selectedFare ? (
//             <span style={{ color: "#16a34a", fontWeight: "500" }}>
//               ✓ Fare selected — ready to book
//             </span>
//           ) : (
//             "Select a fare option above to continue"
//           )}
//         </div>
//         <Button
//           onClick={handleContinue}
//           disabled={!canContinue || loading}
//           style={{
//             background: canContinue ? "linear-gradient(135deg, #0066cc, #004999)" : "#ccc",
//             border: "none",
//             borderRadius: "8px",
//             padding: "10px 28px",
//             fontWeight: "600",
//             fontSize: "14px",
//             color: canContinue ? "#fff" : "#666",
//             cursor: canContinue ? "pointer" : "not-allowed",
//           }}
//         >
//           Continue Booking →
//         </Button>
//       </Modal.Footer>
//     </Modal>
//   );
// };

// export default FlightDetail;